// Week timeline data for the full report: each person's shifts laid out Monday to Sunday,
// one row per day, so a short rest gap, a missing day off or a late finish is visible at a
// glance. Built in the worker (Luxon, Europe/London) and sent to the page as plain data.

import { DateTime } from 'luxon';
import { localDate } from '../model/time';
import type { CanonicalRota, IsoDate, Shift } from '../model/types';
import { ageStatus } from '../rules/age';
import { SEVERITY_ORDER, type Finding, type Severity } from '../rules/types';

export const DAY_MINUTES = 24 * 60;

export interface TimelineBar {
  shiftId: string;
  /** Minutes from local midnight, 0 to 1440, for the part of the shift on this day. */
  from: number;
  to: number;
  /** The whole shift, e.g. "Sat 7 Nov, 17:00 to Sun 8 Nov, 00:30", for labels. */
  label: string;
  breakMinutes?: number;
  /** The most serious finding that names this shift, if any. */
  severity?: Severity;
  /** The shift started the day before or carries on into the next day. */
  continuesBefore: boolean;
  continuesAfter: boolean;
}

export interface TimelineDay {
  date: IsoDate;
  /** "Mon 2 Nov" */
  label: string;
  bars: TimelineBar[];
}

export interface TimelineWeek {
  monday: IsoDate;
  /** "Week starting Monday 2 November" */
  label: string;
  days: TimelineDay[];
}

/** The night window shaded on a person's timeline. `from` > `to` means it wraps past midnight. */
export interface NightBand {
  from: number;
  to: number;
  label: string;
}

const DEFAULT_NIGHT = { start: '23:00', end: '06:00' };

/**
 * The window that matters for this person, using the same definitions as the rules:
 * for anyone under 18 on a shift date, the reg 6A restricted period (22:00–06:00, or
 * 23:00–07:00 if their contract allows work after 10pm, D22); otherwise the reg 2 night
 * period (the site's agreed one, or 23:00–06:00).
 */
export function nightBand(rota: CanonicalRota, employeeRef: string, zone: string): NightBand {
  const employee = rota.employees.find((e) => e.ref === employeeRef);
  const shifts = rota.shifts.filter((s) => s.employeeRef === employeeRef);
  const young =
    employee !== undefined &&
    shifts.some((s) => ageStatus(employee, localDate(s.start, zone)) === 'young');
  if (young) {
    const [from, to] = employee.youngWorkerContractAfter22 === true ? [23, 7] : [22, 6];
    return {
      from: from * 60,
      to: to * 60,
      label: `Shaded: ${hourText(from)} to ${hourText(to)}, when under-18s usually can't work`,
    };
  }
  const siteRef = shifts[0]?.siteRef;
  const agreed = rota.sites.find((s) => s.ref === siteRef)?.agreedNightPeriod;
  const period = agreed ?? DEFAULT_NIGHT;
  const from = clockMinutes(period.start);
  const to = clockMinutes(period.end);
  return {
    from,
    to,
    label: `Shaded: the night period, ${clockText(from)} to ${clockText(to)}${agreed ? ' (as agreed for this site)' : ''}`,
  };
}

function clockMinutes(time: string): number {
  const [h = 0, m = 0] = time.split(':').map(Number);
  return h * 60 + m;
}

function hourText(hour: number): string {
  return hour < 12 ? `${hour}am` : `${hour - 12}pm`;
}

function clockText(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0
    ? hourText(h)
    : `${h % 12 || 12}:${String(m).padStart(2, '0')}${h < 12 ? 'am' : 'pm'}`;
}

/** One person's weeks across the rota period. Days outside the period are left out. */
export function personTimeline(
  rota: CanonicalRota,
  employeeRef: string,
  findings: Finding[],
  zone: string,
): TimelineWeek[] {
  const worst = worstSeverityByShift(findings.filter((f) => f.employeeRef === employeeRef));
  const shifts = rota.shifts.filter(
    (s) => s.employeeRef === employeeRef && (s.status ?? 'assigned') === 'assigned',
  );

  const first = DateTime.fromISO(rota.period.start, { zone });
  const last = DateTime.fromISO(rota.period.end, { zone });
  if (!first.isValid || !last.isValid || last < first) return [];

  const weeks: TimelineWeek[] = [];
  for (let monday = first.startOf('week'); monday <= last; monday = monday.plus({ weeks: 1 })) {
    const days: TimelineDay[] = [];
    for (let i = 0; i < 7; i++) {
      const day = monday.plus({ days: i });
      if (day < first || day > last) continue;
      days.push({
        date: day.toISODate() ?? '',
        label: day.toFormat('ccc d LLL'),
        bars: barsOnDay(shifts, day, worst, zone),
      });
    }
    weeks.push({
      monday: monday.toISODate() ?? '',
      label: `Week starting ${monday.toFormat('cccc d LLLL')}`,
      days,
    });
  }
  return weeks;
}

/** The parts of these shifts that fall on one local day, earliest first. */
export function barsOnDay(
  shifts: Shift[],
  day: DateTime,
  worst: Map<string, Severity>,
  zone: string,
): TimelineBar[] {
  const dayStart = day.startOf('day');
  const dayEnd = dayStart.plus({ days: 1 });
  const bars: TimelineBar[] = [];
  for (const shift of shifts) {
    const start = DateTime.fromISO(shift.start, { zone });
    const end = DateTime.fromISO(shift.end, { zone });
    if (end <= dayStart || start >= dayEnd) continue;
    const severity = worst.get(shift.id);
    bars.push({
      shiftId: shift.id,
      from: start <= dayStart ? 0 : wallMinutes(start),
      to: end >= dayEnd ? DAY_MINUTES : wallMinutes(end),
      label: `${start.toFormat('ccc d LLL, HH:mm')} to ${end.toFormat(end.hasSame(start, 'day') ? 'HH:mm' : 'ccc d LLL, HH:mm')}`,
      ...(shift.breakMinutes !== undefined && { breakMinutes: shift.breakMinutes }),
      ...(severity !== undefined && { severity }),
      continuesBefore: start < dayStart,
      continuesAfter: end > dayEnd,
    });
  }
  return bars.sort((a, b) => a.from - b.from);
}

/**
 * Minutes since local midnight by the clock on the wall, so a shift shown as 17:00 is drawn
 * at 17:00 even on a clock-change day.
 */
function wallMinutes(time: DateTime): number {
  return time.hour * 60 + time.minute;
}

function worstSeverityByShift(findings: Finding[]): Map<string, Severity> {
  const worst = new Map<string, Severity>();
  for (const f of findings) {
    for (const id of f.shiftIds) {
      const current = worst.get(id);
      if (current === undefined || SEVERITY_ORDER[f.severity] < SEVERITY_ORDER[current])
        worst.set(id, f.severity);
    }
  }
  return worst;
}
